import { useState } from 'react';
import { 
  MessageCircle, Send, Camera, MapPin, Calendar, Clock,
  Music, Coffee, Utensils, ShoppingBag, Info, ChevronRight,
  Sparkles, Search, Mic, Image, Bot, Star, TrendingUp,
  Home, Wallet, User, ArrowLeft, MoreVertical, ThumbsUp,
  Map, Phone, Globe, Building, Smile
} from 'lucide-react';
import { components, gradients, colors } from '../theme/wunderCityTheme';

function PensacolaAI() {
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);

  const suggestions = [
    { icon: Music, text: "Live music tonight", color: "emerald" },
    { icon: Coffee, text: "Coffee shops open now", color: "amber" },
    { icon: Utensils, text: "Best seafood restaurants", color: "sky" },
    { icon: Calendar, text: "Weekend events", color: "pink" }
  ];

  const recentQueries = [
    "What time does Palafox Market close?",
    "Where can I find the best key lime pie?",
    "Are there any art galleries open late?"
  ];

  const chatMessages = [
    {
      type: 'user',
      text: "What's happening downtown tonight?",
      time: "7:32 PM"
    },
    {
      type: 'ai',
      text: "Great evening for downtown! Here's what's happening:",
      cards: [
        {
          title: "Live Jazz at Seville Quarter",
          subtitle: "8 PM - Midnight • $10 cover",
          icon: Music,
          color: "emerald"
        },
        {
          title: "Art Walk on Palafox",
          subtitle: "5-9 PM • Free admission",
          icon: Calendar,
          color: "pink"
        }
      ],
      time: "7:32 PM"
    }
  ];

  return (
    <div className="w-full">
      {/* Section Header */}
      <div className="text-center px-4 md:px-8 pb-8 md:pb-12">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
          Wunder City's Pensacola AI Chatbot
        </h2>
        <p className="text-lg md:text-xl text-gray-600">
          Instant Access to a VIP Pensacola Concierge
        </p>
      </div>

      {/* AI Screens */}
      <div className="flex flex-col lg:flex-row gap-8 p-4 md:p-8 items-center lg:justify-center overflow-x-auto">
        
        {/* Solution 1: Quick Discovery */}
        <div className="flex flex-col items-center gap-1 md:gap-2 lg:gap-4">
          <div className="relative flex-shrink-0 h-[609px] md:h-[731px] lg:h-[812px] flex items-center justify-center">
            <div className={`transform ${components.phoneMockup.scale.mobile} ${components.phoneMockup.scale.tablet} ${components.phoneMockup.scale.desktop} origin-center`}>
              <div className={components.phoneMockup.outer}>
                <div className={components.phoneMockup.inner}>
                  {/* Status Bar */}
                  <div className={components.statusBar.container}>
                    <span className={components.statusBar.time}>9:41</span>
                    <div className={components.statusBar.battery}>
                      <div className="w-4 h-3 bg-black rounded-sm"></div>
                      <div className="w-4 h-3 bg-black rounded-sm"></div>
                      <div className="w-4 h-3 bg-black rounded-sm"></div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="pt-11 h-full bg-gradient-to-b from-purple-50 to-white flex flex-col">
                    {/* Header */}
                    <div className="px-6 pt-4 pb-6">
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
                            <Bot className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h1 className="text-xl font-bold text-gray-900">Pensacola AI</h1>
                            <p className="text-sm text-gray-600">Your local guide</p>
                          </div>
                        </div>
                        <button className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                          <MoreVertical className="w-5 h-5 text-gray-600" />
                        </button>
                      </div>

                      {/* Quick Actions Grid */}
                      <div className="grid grid-cols-2 gap-3">
                        {suggestions.map((suggestion, index) => {
                          const Icon = suggestion.icon;
                          const bgColor = {
                            emerald: 'bg-emerald-100',
                            amber: 'bg-amber-100',
                            sky: 'bg-sky-100',
                            pink: 'bg-pink-100'
                          }[suggestion.color];
                          const iconColor = {
                            emerald: 'text-emerald-600',
                            amber: 'text-amber-600',
                            sky: 'text-sky-600',
                            pink: 'text-pink-600'
                          }[suggestion.color];

                          return (
                            <button
                              key={index}
                              className={`${bgColor} rounded-2xl p-4 flex flex-col items-start gap-3 transition-all hover:scale-[0.98]`}
                            >
                              <Icon className={`w-6 h-6 ${iconColor}`} />
                              <span className="text-sm font-medium text-gray-900 text-left">
                                {suggestion.text}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Search Bar */}
                    <div className="px-6 mb-6">
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Ask me anything about Pensacola..."
                          className="w-full bg-white rounded-2xl pl-12 pr-12 py-4 text-gray-900 placeholder-gray-500 shadow-sm border border-gray-100"
                        />
                        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <button className="absolute right-4 top-1/2 transform -translate-y-1/2">
                          <Mic className="w-5 h-5 text-gray-400" />
                        </button>
                      </div>
                    </div>

                    {/* Recent Queries */}
                    <div className="flex-1 px-6 overflow-y-auto pb-20">
                      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                        Recent Questions
                      </h3>
                      <div className="space-y-3">
                        {recentQueries.map((query, index) => (
                          <button
                            key={index}
                            className="w-full bg-white rounded-2xl p-4 flex items-center justify-between group hover:shadow-md transition-all"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
                                <Clock className="w-5 h-5 text-gray-600" />
                              </div>
                              <p className="text-left text-gray-900">{query}</p>
                            </div>
                            <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
                          </button>
                        ))}
                      </div>

                      {/* Popular Topics */}
                      <div className="mt-8">
                        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                          Popular Topics
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {['History', 'Beaches', 'Dining', 'Events', 'Shopping', 'Nightlife'].map((topic) => (
                            <button
                              key={topic}
                              className="bg-gray-100 text-gray-700 px-4 py-2 rounded-full text-sm font-medium hover:bg-gray-200 transition-colors"
                            >
                              {topic}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Bottom Action Bar */}
                    <div className="absolute bottom-20 left-0 right-0 bg-white border-t border-gray-100 px-6 py-4">
                      <button className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 text-white py-3 rounded-2xl font-semibold flex items-center justify-center gap-2 shadow-lg">
                        <Camera className="w-5 h-5" />
                        Identify with Photo
                      </button>
                    </div>

                    {/* Tab Bar */}
                    <div className={components.tabBar.container}>
                      <div className="flex items-center justify-around py-2">
                        <button className="flex flex-col items-center gap-1 py-2 px-4">
                          <Home className="w-6 h-6 text-gray-400" />
                          <span className="text-xs text-gray-600">Home</span>
                        </button>
                        <button className="flex flex-col items-center gap-1 py-2 px-4">
                          <Search className="w-6 h-6 text-gray-400" />
                          <span className="text-xs text-gray-600">Discover</span>
                        </button>
                        <button className="flex flex-col items-center gap-1 py-2 px-4">
                          <Wallet className="w-6 h-6 text-gray-400" />
                          <span className="text-xs text-gray-600">Wallet</span>
                        </button>
                        <button className="flex flex-col items-center gap-1 py-2 px-4">
                          <User className="w-6 h-6 text-gray-400" />
                          <span className="text-xs text-gray-600">Profile</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <p className={`${components.mockupLabel.className} ${components.mockupLabel.spacing.top} ${components.mockupLabel.spacing.bottom}`}>Discovery Dashboard</p>
        </div>

        {/* Solution 2: Conversational Chat */}
        <div className="flex flex-col items-center gap-1 md:gap-2 lg:gap-4">
          <div className="relative flex-shrink-0 h-[609px] md:h-[731px] lg:h-[812px] flex items-center justify-center">
            <div className={`transform ${components.phoneMockup.scale.mobile} ${components.phoneMockup.scale.tablet} ${components.phoneMockup.scale.desktop} origin-center`}>
              <div className={components.phoneMockup.outer}>
                <div className={components.phoneMockup.inner}>
                  {/* Status Bar */}
                  <div className={components.statusBar.container}>
                    <span className={components.statusBar.time}>9:41</span>
                    <div className={components.statusBar.battery}>
                      <div className="w-4 h-3 bg-black rounded-sm"></div>
                      <div className="w-4 h-3 bg-black rounded-sm"></div>
                      <div className="w-4 h-3 bg-black rounded-sm"></div>
                    </div>
                  </div>

                  {/* Chat Interface */}
                  <div className="pt-11 h-full bg-gray-50 flex flex-col">
                    {/* Chat Header */}
                    <div className="bg-white px-6 py-4 border-b border-gray-100">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <button className="w-8 h-8 rounded-full flex items-center justify-center">
                            <ArrowLeft className="w-5 h-5 text-gray-700" />
                          </button>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center">
                              <Bot className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <h2 className="font-semibold text-gray-900">Pensacola AI</h2>
                              <div className="flex items-center gap-1">
                                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                <p className="text-xs text-gray-600">Always here to help</p>
                              </div>
                            </div>
                          </div>
                        </div>
                        <button className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                          <Info className="w-5 h-5 text-gray-600" />
                        </button>
                      </div>
                    </div>

                    {/* Chat Messages */}
                    <div className="flex-1 overflow-y-auto px-6 py-4">
                      {chatMessages.map((message, index) => (
                        <div key={index} className={`mb-4 ${message.type === 'user' ? 'flex justify-end' : ''}`}>
                          {message.type === 'user' ? (
                            <div className="max-w-[80%]">
                              <div className="bg-sky-500 text-white rounded-2xl rounded-br-sm px-4 py-3">
                                <p className="text-sm">{message.text}</p>
                              </div>
                              <p className="text-xs text-gray-500 mt-1 text-right">{message.time}</p>
                            </div>
                          ) : (
                            <div className="max-w-[80%]">
                              <div className="flex items-start gap-2 mb-2">
                                <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                                  <Bot className="w-4 h-4 text-emerald-600" />
                                </div>
                                <div className="flex-1">
                                  <div className="bg-white rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
                                    <p className="text-sm text-gray-900">{message.text}</p>
                                  </div>
                                  {message.cards && (
                                    <div className="mt-3 space-y-2">
                                      {message.cards.map((card, cardIndex) => {
                                        const Icon = card.icon;
                                        const bgColor = card.color === 'emerald' ? 'bg-emerald-50' : 'bg-pink-50';
                                        const iconColor = card.color === 'emerald' ? 'text-emerald-600' : 'text-pink-600';
                                        
                                        return (
                                          <button
                                            key={cardIndex}
                                            className={`w-full ${bgColor} rounded-2xl p-4 flex items-start gap-3 hover:shadow-md transition-all`}
                                          >
                                            <div className={`w-10 h-10 ${card.color === 'emerald' ? 'bg-emerald-100' : 'bg-pink-100'} rounded-xl flex items-center justify-center flex-shrink-0`}>
                                              <Icon className={`w-5 h-5 ${iconColor}`} />
                                            </div>
                                            <div className="flex-1 text-left">
                                              <h4 className="font-semibold text-gray-900 mb-1">{card.title}</h4>
                                              <p className="text-sm text-gray-600">{card.subtitle}</p>
                                            </div>
                                            <ChevronRight className="w-5 h-5 text-gray-400" />
                                          </button>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <p className="text-xs text-gray-500 mt-1 ml-10">{message.time}</p>
                            </div>
                          )}
                        </div>
                      ))}
                      
                      {/* Typing Indicator */}
                      <div className="flex items-start gap-2">
                        <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                          <Bot className="w-4 h-4 text-emerald-600" />
                        </div>
                        <div className="bg-white rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
                          <div className="flex gap-1">
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Suggestion Chips */}
                    <div className="px-6 py-3 bg-white border-t border-gray-100">
                      <div className="flex gap-2 overflow-x-auto pb-2">
                        <button className="flex-shrink-0 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium">
                          Show more events
                        </button>
                        <button className="flex-shrink-0 bg-gray-100 text-gray-700 px-4 py-2 rounded-full text-sm font-medium">
                          Restaurant hours
                        </button>
                        <button className="flex-shrink-0 bg-gray-100 text-gray-700 px-4 py-2 rounded-full text-sm font-medium">
                          Parking info
                        </button>
                      </div>
                    </div>

                    {/* Input Bar */}
                    <div className="bg-white border-t border-gray-100 px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                          <Image className="w-5 h-5 text-gray-600" />
                        </button>
                        <div className="flex-1 relative">
                          <input
                            type="text"
                            placeholder="Type your question..."
                            className="w-full bg-gray-100 rounded-full pl-4 pr-4 py-2.5 text-gray-900 placeholder-gray-500"
                          />
                        </div>
                        <button className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center">
                          <Send className="w-5 h-5 text-white" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <p className={`${components.mockupLabel.className} ${components.mockupLabel.spacing.top} ${components.mockupLabel.spacing.bottom}`}>Chat Interface</p>
        </div>

        {/* Solution 3: Visual Explorer */}
        <div className="flex flex-col items-center gap-1 md:gap-2 lg:gap-4">
          <div className="relative flex-shrink-0 h-[609px] md:h-[731px] lg:h-[812px] flex items-center justify-center">
            <div className={`transform ${components.phoneMockup.scale.mobile} ${components.phoneMockup.scale.tablet} ${components.phoneMockup.scale.desktop} origin-center`}>
              <div className={components.phoneMockup.outer}>
                <div className={components.phoneMockup.inner}>
                  {/* Status Bar */}
                  <div className={components.statusBar.container}>
                    <span className={components.statusBar.time}>9:41</span>
                    <div className={components.statusBar.battery}>
                      <div className="w-4 h-3 bg-black rounded-sm"></div>
                      <div className="w-4 h-3 bg-black rounded-sm"></div>
                      <div className="w-4 h-3 bg-black rounded-sm"></div>
                    </div>
                  </div>

                  {/* Visual Explorer */}
                  <div className="pt-11 h-full bg-white flex flex-col">
                    {/* Header */}
                    <div className="px-6 pt-4 pb-4">
                      <div className="flex items-center justify-between mb-4">
                        <h1 className="text-2xl font-bold text-gray-900">Explore Pensacola</h1>
                        <div className="flex items-center gap-2">
                          <button className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                            <Bot className="w-5 h-5 text-emerald-600" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* AI Assistant Banner */}
                    <div className="px-6 mb-4">
                      <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-3xl p-5 text-white">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                            <Sparkles className="w-6 h-6 text-white" />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold mb-1">Ask Pensacola AI</h3>
                            <p className="text-emerald-100 text-sm mb-3">Get instant answers about our city</p>
                            <div className="flex gap-2">
                              <button className="bg-white/20 backdrop-blur text-white px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1">
                                <MessageCircle className="w-4 h-4" />
                                Chat
                              </button>
                              <button className="bg-white/20 backdrop-blur text-white px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1">
                                <Camera className="w-4 h-4" />
                                Identify
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Visual Categories */}
                    <div className="flex-1 overflow-y-auto pb-20">
                      {/* Trending Searches */}
                      <div className="px-6 mb-6">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-semibold text-gray-900">Trending Now</h3>
                          <TrendingUp className="w-5 h-5 text-gray-400" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="relative h-32 bg-gray-200 rounded-2xl overflow-hidden">
                            <img 
                              src="https://images.unsplash.com/photo-1514933651103-005eec06c04b?w=400" 
                              alt="Live Music"
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-4">
                              <div>
                                <h4 className="text-white font-semibold">Live Music Venues</h4>
                                <p className="text-white/80 text-xs">8 spots tonight</p>
                              </div>
                            </div>
                          </div>
                          <div className="relative h-32 bg-gray-200 rounded-2xl overflow-hidden">
                            <img 
                              src="https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=400" 
                              alt="Restaurants"
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-4">
                              <div>
                                <h4 className="text-white font-semibold">Top Restaurants</h4>
                                <p className="text-white/80 text-xs">Open now</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* AI Insights */}
                      <div className="px-6 mb-6">
                        <h3 className="font-semibold text-gray-900 mb-3">AI Insights</h3>
                        <div className="space-y-3">
                          <div className="bg-amber-50 rounded-2xl p-4 flex items-start gap-3">
                            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                              <Star className="w-5 h-5 text-amber-600" />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-medium text-gray-900 mb-1">Perfect Weather Alert</h4>
                              <p className="text-sm text-gray-600">Great day for beach activities! 78°F with light breeze.</p>
                            </div>
                          </div>
                          <div className="bg-emerald-50 rounded-2xl p-4 flex items-start gap-3">
                            <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                              <Calendar className="w-5 h-5 text-emerald-600" />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-medium text-gray-900 mb-1">Gallery Night Tomorrow</h4>
                              <p className="text-sm text-gray-600">Monthly art walk featuring 20+ galleries.</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Quick Answers */}
                      <div className="px-6">
                        <h3 className="font-semibold text-gray-900 mb-3">Quick Answers</h3>
                        <div className="grid grid-cols-2 gap-2">
                          {[
                            { icon: Building, text: "Historic Sites" },
                            { icon: Map, text: "Walking Tours" },
                            { icon: Coffee, text: "Coffee Shops" },
                            { icon: Smile, text: "Family Fun" },
                            { icon: Globe, text: "Museums" },
                            { icon: Phone, text: "Emergency" }
                          ].map((item, index) => {
                            const Icon = item.icon;
                            return (
                              <button
                                key={index}
                                className="bg-gray-50 rounded-xl p-3 flex items-center gap-3 hover:bg-gray-100 transition-colors"
                              >
                                <Icon className="w-5 h-5 text-gray-600" />
                                <span className="text-sm font-medium text-gray-900">{item.text}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Floating AI Button */}
                    <div className="absolute bottom-24 right-6">
                      <button className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full shadow-lg flex items-center justify-center">
                        <MessageCircle className="w-6 h-6 text-white" />
                      </button>
                    </div>

                    {/* Tab Bar */}
                    <div className={components.tabBar.container}>
                      <div className="flex items-center justify-around py-2">
                        <button className="flex flex-col items-center gap-1 py-2 px-4">
                          <Home className="w-6 h-6 text-gray-400" />
                          <span className="text-xs text-gray-600">Home</span>
                        </button>
                        <button className="flex flex-col items-center gap-1 py-2 px-4">
                          <Search className="w-6 h-6 text-emerald-600" />
                          <span className="text-xs text-emerald-600">Explore</span>
                        </button>
                        <button className="flex flex-col items-center gap-1 py-2 px-4">
                          <Wallet className="w-6 h-6 text-gray-400" />
                          <span className="text-xs text-gray-600">Wallet</span>
                        </button>
                        <button className="flex flex-col items-center gap-1 py-2 px-4">
                          <User className="w-6 h-6 text-gray-400" />
                          <span className="text-xs text-gray-600">Profile</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <p className={`${components.mockupLabel.className} ${components.mockupLabel.spacing.top} ${components.mockupLabel.spacing.bottom}`}>Visual Explorer</p>
        </div>

      </div>
    </div>
  );
}

export default PensacolaAI;